import {
  Spacer,
  Heading,
  Grid,
  Text,
  Tooltip,
} from '@oliasoft-open-source/react-ui-library';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { displayNumber, round } from '@oliasoft-open-source/units';
import translations from 'client/internationalization/translation-map.json';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { RootState } from 'client/store/configureStore';
import { getCompanyUsers } from 'client/store/entities/user/selectors';
import { EntityMetadataType, metadataLabelMap } from 'common/enums/entity-enums';

const BYTES_THRESHOLD = 1024;

type TMetadataComponentProps = PropsFromRedux & { dataObject: IObject };

const MetadataComponent = ({ dataObject, users }: TMetadataComponentProps) => {
  const { t } = useTranslation();
  const userData = users.find(user => user.subjectId === dataObject.owner);
  const formatBytes = (bytes: number) => {
    if (bytes < BYTES_THRESHOLD) {
      return <Text>{`${displayNumber(bytes, { scientific: false })} bytes`}</Text>;
    }
    const kiloBytes = bytes / 1024;
    if (kiloBytes < BYTES_THRESHOLD) {
      return <Tooltip text={`${displayNumber(bytes, { scientific: false })} bytes`}><Text>{`${displayNumber(round(kiloBytes, 2), { scientific: false })} KB`}</Text></Tooltip>;
    }
    const megaBytes = kiloBytes / 1024;
    if (megaBytes < BYTES_THRESHOLD) {
      return <Tooltip text={`${displayNumber(bytes, { scientific: false })} bytes`}><Text>{`${displayNumber(round(megaBytes, 2), { scientific: false })} MB`}</Text></Tooltip>;
    }
    const gigaBytes = megaBytes / 1024;
    if (gigaBytes < BYTES_THRESHOLD) {
      return <Tooltip text={`${displayNumber(bytes, { scientific: false })} bytes`}><Text>{`${displayNumber(round(gigaBytes, 2), { scientific: false })} GB`}</Text></Tooltip>;
    }
  };

  const author = userData?.lastName && (
    <>
      <Text bold>{t(translations.author)}</Text>
      <Text>{`${userData?.firstName} ${userData?.lastName}`}</Text>
    </>
  );

  const size = dataObject.data?.rows?.length || dataObject.metadata?.file?.size ? (
    <>
      <Text bold>{t(translations.size)}</Text>
      <Text>{dataObject.data?.rows?.length || formatBytes(dataObject.metadata?.file?.size)}</Text>
    </>
  ) : null;

  const created = dataObject.created ? (
    <>
      <Text bold>{t(translations.created)}</Text>
      <Text>
        {new Date(dataObject.created).toLocaleString('en-GB', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })}
      </Text>
    </>
  ) : null;
  const updated = dataObject.updated ? (
    <>
      <Text bold>{t(translations.lastUpdated)}</Text>
      <Text>
        {new Date(dataObject.synced).toLocaleString('en-GB', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })}
      </Text>
    </>
  ) : null;
  const source = dataObject.metadata?.source ? (
    <>
      <Text bold>{t(translations.source)}</Text>
      <Text>{dataObject.metadata.source}</Text>
    </>
  ) : null;
  const fileName = dataObject.metadata?.file?.name ? (
    <>
      <Text bold>{t(translations.fileName)}</Text>
      <Text>{dataObject.metadata.file.name}</Text>
    </>
  ) : null;
  const fileType = dataObject.metadata?.file?.fileType ? (
    <>
      <Text bold>{t(translations.fileType)}</Text>
      <Text>{dataObject.metadata.file.fileType}</Text>
    </>
  ) : null;

  const designid = dataObject?.metadata?.hierarchyData?.design?.id ? (
    <>
      <Text bold>Design id</Text>
      <Text>{dataObject?.metadata?.hierarchyData?.design?.id}</Text>
    </>
  ) : null;
  const objectid = (
    <>
      <Text bold>Object id</Text>
      <Text>{dataObject.objectid}</Text>
    </>
  );

  const objectype = (
    <>
      <Text bold>{t(translations.type)}</Text>
      <Text>{dataObject.type}</Text>
    </>
  );
  const metadataFields = Object.values(EntityMetadataType).map(field => {
    if (dataObject?.metadata?.[field]) {
      return (
        <>
          <Text bold>{metadataLabelMap[field]}</Text>
          <Text>{dataObject?.metadata?.[field]}</Text>
        </>
      );
    }
    return null;
  }).filter(Boolean);
  return (
    <div>
      <Heading>{t(translations.metadata)}</Heading>
      <Spacer />
      <Grid columns="32% 68%" gap="var(--padding-xs)">
        {fileName}
        {fileType}
        {size}
        {created}
        {updated}
        {source}
        {objectid}
        {objectype}
        {designid}
        {author}
        {...metadataFields}
      </Grid>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getCompanyUsers(state),
});

const connector = connect(mapStateToProps);
const Container = connector(MetadataComponent);

type PropsFromRedux = ConnectedProps<typeof connector>;

export { Container as MetadataComponent };
